import { Button, Checkbox, Group, Input, Radio, TextInput } from '@any-ui-react/core';
import classNames from 'classnames';
import subDays from 'date-fns/subDays';
import isEmpty from 'lodash-es/isEmpty';
import sortBy from 'lodash-es/sortBy';
import trim from 'lodash-es/trim';
import { Fragment, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Link } from 'react-router';
import { TooltipGreenhouseLink } from '@/common/components/employees/shared/TooltipGreenhouseLink';
import { TooltipHiringSource } from '@/common/components/employees/shared/TooltipHiringSource';
import { DatePicker } from '@/common/components/shared/date-picker/DatePicker';
import { EmployeeLink } from '@/common/components/shared/link/EmployeeLink';
import { MaterialIcons } from '@/common/components/shared/material-icons/MaterialIcons';
import { CompanyEmailDomainSelectSingle } from '@/common/components/shared/select/company-email-domain/CompanyEmailDomainSelect';
import { EmployeeHiringSourceSelectSingle } from '@/common/components/shared/select/employee-hiring-source/EmployeeHiringSourceSelect';
import { NationalitySelectSingle } from '@/common/components/shared/select/nationality/NationalitySelect';
import { EMPTY_DEFAULT } from '@/common/components/shared/table/constants';
import { useEmployeeDetailContext } from '@/common/contexts/EmployeeDetailContext';
import { ContractType } from '@/common/graphql';
import type { EmailCheckStatus } from '@/common/hooks/employees/useCheckCompanyEmailAvailability';
import { useContractTypePersistence } from '@/common/hooks/employees/useContractTypePersistence';
import { type EmployeeGeneralInformationFormDataType, FormAction } from '@/common/types';
import { DateUtils, StringUtils } from '@/common/utils';
import { Managers } from './Managers';

const EMAIL_CHECK_MESSAGES: Record<EmailCheckStatus, string> = {
  idle: '',
  loading: '',
  available: 'This email address is available.',
  in_use_med: 'This email address is already used by another employee. Please use a different email address.',
  in_use_google_workspace:
    'This email address is already registered in Google Workspace. Please use a different email address.',
  invalid_format: 'The email address format is invalid.',
  api_error: 'Unable to verify email address. Please try again later.',
};

const GW_EDITING_MESSAGE =
  'This email address is already registered in Google Workspace. You can use it but this employee will be linked with this email address.';

interface Props {
  mode?: FormAction;
  emailCheckStatus?: EmailCheckStatus;
  clearingEmployeeIds?: string[];
  onCheckAvailability?: () => Promise<void>;
}

export const GeneralInformationFormFields = ({
  mode = FormAction.CREATING,
  emailCheckStatus,
  clearingEmployeeIds,
  onCheckAvailability,
}: Props) => {
  const {
    control,
    formState: { errors, dirtyFields },
    register,
    setValue,
    watch,
    trigger,
  } = useFormContext<EmployeeGeneralInformationFormDataType>();

  const hiredDate = useWatch({ control, name: 'hiredDate' });
  const probationReviewEndDate = useWatch({ control, name: 'probationReviewEndDate' });
  const isNewGraduate = useWatch({ control, name: 'isNewGraduate' });
  const companyEmailAccount = useWatch({ control, name: 'companyEmailAccount' });
  const companyEmailDomain = useWatch({ control, name: 'companyEmailDomain' });
  const greenhouseLink = useWatch({ control, name: 'greenhouseLink' });
  const contractType = useWatch({ control, name: 'contractType' });

  const { employeeDetail } = useEmployeeDetailContext();
  const { data: allContractTypes } = useContractTypePersistence();

  const contractHistories = useMemo(() => employeeDetail.contractHistories, [employeeDetail.contractHistories]);
  const preAcquiredContract = useMemo(() => {
    if (contractHistories.length === 0) return undefined;
    const firstContractStartDate = sortBy(contractHistories, 'effectiveDateStart')[0].effectiveDateStart;
    return contractHistories.find(it => it.effectiveDateStart === firstContractStartDate && it.isPreAcquisition);
  }, [contractHistories]);

  const hiredDateMinDate = useMemo(() => {
    return contractHistories.length === 0
      ? DateUtils.dateAnyMindStarts
      : !preAcquiredContract
        ? DateUtils.dateAnyMindStarts
        : undefined;
  }, [contractHistories, preAcquiredContract]);

  const hiredDateMaxDate = useMemo(() => {
    if (contractHistories.length === 0) {
      return undefined;
    } else if (preAcquiredContract?.companyAcquiredDate) {
      const parsed = DateUtils.parseDate(preAcquiredContract.companyAcquiredDate);
      return parsed ? subDays(parsed, 1) : undefined;
    } else {
      return undefined;
    }
  }, [contractHistories, preAcquiredContract]);

  const { text: hiredDateText, isFuture: hiredDateIsFuture } = useMemo(
    () => DateUtils.getDifferenceString(hiredDate),
    [hiredDate],
  );

  const { text: probationReviewEndDateText, isFuture: probationReviewEndDateIsFuture } = useMemo(
    () => DateUtils.getDifferenceString(probationReviewEndDate),
    [probationReviewEndDate],
  );

  const showEmailCheck = useMemo(
    () =>
      !isEmpty(trim(companyEmailAccount ?? '')) &&
      !isEmpty(trim(companyEmailDomain ?? '')) &&
      (mode === FormAction.CREATING ||
        (mode === FormAction.EDITING && (dirtyFields.companyEmailAccount || dirtyFields.companyEmailDomain))),
    [companyEmailAccount, companyEmailDomain, mode, dirtyFields.companyEmailAccount, dirtyFields.companyEmailDomain],
  );

  const { text: employmentEndDateText, isFuture: employmentEndDateIsFuture } = useMemo(() => {
    const employmentEndDate = employeeDetail.offBoarding.employmentEndDate;
    if (!employmentEndDate) {
      return { text: '', isFuture: true };
    }

    return DateUtils.getDifferenceString(employmentEndDate);
  }, [employeeDetail.offBoarding.employmentEndDate]);

  return (
    <fieldset className="form-fieldset grid-cols-12">
      <TextInput
        {...register('preferredFullName')}
        autoComplete="off"
        className="col-span-12 md:col-span-6"
        data-1p-ignore={true}
        error={errors.preferredFullName?.message}
        label="Preferred Full Name in English"
        placeholder="Preferred Full Name in English"
        required={true}
      />
      <TextInput
        {...register('legalFullName')}
        autoComplete="off"
        className="col-span-12 md:col-span-6"
        data-1p-ignore={true}
        error={errors.legalFullName?.message}
        label="Legal Full Name in English"
        placeholder="Legal Full Name in English"
        required={true}
      />
      <Controller
        control={control}
        name="nationalityId"
        render={({ field }) => (
          <div className="col-span-12 md:col-span-4 xl:col-span-6">
            <NationalitySelectSingle
              {...field}
              error={errors.nationalityId?.message}
              label="Nationality"
              placeholder="Nationality"
              required={true}
              onChange={option => setValue('nationalityId', option?.value as string, { shouldDirty: true })}
            />
          </div>
        )}
      />
      <TextInput
        {...register('nameInLocalLanguage')}
        autoComplete="off"
        className="col-span-12 sm:col-span-6 md:col-span-4 xl:col-span-3"
        data-1p-ignore={true}
        error={errors.nameInLocalLanguage?.message}
        label="Name in Local Language"
        placeholder="Name in Local Language"
      />
      <TextInput
        {...register('japanesePhoneticName')}
        autoComplete="off"
        className="col-span-12 sm:col-span-6 md:col-span-4 xl:col-span-3"
        data-1p-ignore={true}
        inputWrapperOrder={['label', 'input', 'description', 'error']}
        label="ﾌﾘｶﾞﾅ (Kana of Japanese)"
        placeholder="ﾌﾘｶﾞﾅ (Kana of Japanese)"
      />

      <Managers control={control} employeeId={employeeDetail.id} errors={errors} />

      <Controller
        control={control}
        name="hiredDate"
        render={({ field: { value, ...field } }) => (
          <div className="col-span-12 sm:col-span-6">
            <DatePicker
              {...field}
              error={errors.hiredDate?.message}
              label="Hired Date"
              maxDate={hiredDateMaxDate}
              minDate={hiredDateMinDate}
              placeholder="yyyy-MM-dd"
              required={true}
              value={value || undefined}
              onChange={value => setValue('hiredDate', DateUtils.formatDate(value), { shouldDirty: true })}
            />
            <div className="text-gray-7 ml-3 mt-1 text-2xs">
              {hiredDateText}
              {!hiredDateIsFuture ? ' passed' : ''}
            </div>
          </div>
        )}
      />

      <div className="col-span-12 lg:col-span-6">
        <div>
          Company Email&nbsp;
          <span className="text-gray-7 text-xs">(Optional)</span>
        </div>
        <div className="grid sm:grid-cols-2 items-center gap-1">
          <TextInput
            {...register('companyEmailAccount')}
            autoComplete="off"
            data-1p-ignore={true}
            error={errors.companyEmailAccount?.message}
            placeholder="Account"
            required={false}
            onBlur={() => {
              if (
                mode === FormAction.CREATING &&
                watch('contractType') === ContractType.Contractor &&
                watch('companyEmailAccount')?.length
              ) {
                trigger('companyEmailAccount').then();
              }
            }}
          />
          <Controller
            control={control}
            name="companyEmailDomain"
            render={({ field }) => (
              <CompanyEmailDomainSelectSingle
                {...field}
                error={errors.companyEmailDomain?.message}
                leftSection="@"
                placeholder="Email Domain"
                selectableDisabledItems={mode === FormAction.EDITING}
                variables={{ enabled: mode === FormAction.CREATING || null }}
                onChange={option => setValue('companyEmailDomain', option?.value as string, { shouldDirty: true })}
              />
            )}
          />
        </div>
        {mode === FormAction.CREATING &&
          (isEmpty(trim(companyEmailAccount ?? '')) || isEmpty(trim(companyEmailDomain ?? ''))) && (
            <div className="mt-2 flex items-center gap-1 text-xs">
              <MaterialIcons iconName="info" fill={true} className="text-blue-7" />
              Company Email is required to create External Accounts.
            </div>
          )}
        {showEmailCheck && (
          <EmailCheckSection
            emailCheckStatus={emailCheckStatus}
            clearingEmployeeIds={clearingEmployeeIds}
            mode={mode}
            onCheckAvailability={onCheckAvailability}
          />
        )}
        {mode === FormAction.EDITING && (dirtyFields.companyEmailAccount || dirtyFields.companyEmailDomain) && (
          <div className="mt-1 flex items-start gap-1 text-xs">
            <MaterialIcons iconName="info" fill={true} className="text-blue-7 mt-[2px]" />
            <span>
              Editing the email address here will <strong>not</strong> change the email addresses of external accounts.
              If you need to change the primary email address for your Google Workspace account, you will need to edit
              it manually in&nbsp;
              <Link rel="noopener noreferrer" target="_blank" to="admin.google.com/ac/users">
                Google Workspace Admin
              </Link>
              , and then edit it here too.
            </span>
          </div>
        )}
      </div>

      <div className="col-span-12 flex flex-col space-y-1 sm:col-span-6">
        <Input.Label>Employment end date</Input.Label>
        <span>{employeeDetail.offBoarding.employmentEndDate || EMPTY_DEFAULT}</span>
        <div className="text-gray-7">
          {employmentEndDateText}
          {!employmentEndDateIsFuture ? ' passed' : ''}
        </div>
      </div>

      <Controller
        control={control}
        name="needsProbationReview"
        render={({ field: { value, ...fields } }) => (
          <Group className="col-span-12 sm:col-span-6">
            <Radio.Group
              label="Does he/she need Probation review page?"
              {...fields}
              value={value ? '1' : '0'}
              onChange={newValue => setValue('needsProbationReview', newValue === '1', { shouldDirty: true })}
            >
              <div className="mt-2 flex gap-x-4">
                <Radio className="inline" label="Yes" value="1" />
                <Radio className="inline" label="No" value="0" />
              </div>
            </Radio.Group>
          </Group>
        )}
      />

      {mode === FormAction.CREATING ? (
        ''
      ) : (
        <Controller
          control={control}
          name="probationReviewEndDate"
          render={({ field: { value, ...field } }) => (
            <div className="col-span-12 sm:col-span-6">
              <DatePicker
                {...field}
                error={errors.probationReviewEndDate?.message}
                label="Probation End Date"
                minDate={hiredDate ? new Date(hiredDate) : DateUtils.dateAnyMindStarts}
                placeholder="yyyy-MM-dd"
                required={true}
                value={value || undefined}
                onChange={value =>
                  setValue('probationReviewEndDate', DateUtils.formatDate(value), { shouldDirty: true })
                }
              />
              <div className="text-gray-7 ml-3 mt-1 text-2xs">
                {probationReviewEndDateText}
                {!probationReviewEndDateIsFuture ? ' ago' : ''}
              </div>
            </div>
          )}
        />
      )}

      <TextInput
        {...register('greenhouseLink')}
        autoComplete="off"
        className="col-span-12 sm:col-span-6"
        data-1p-ignore={true}
        description={
          greenhouseLink &&
          StringUtils.isUrl(greenhouseLink) && (
            <Link className="inline-flex items-center gap-1 ml-3" target="_blank" to={greenhouseLink}>
              {greenhouseLink}
              <MaterialIcons iconName="open_in_new" className="text-blue-6 cursor-pointer" fontSize="12px" />
            </Link>
          )
        }
        error={errors.greenhouseLink?.message}
        inputWrapperOrder={['label', 'input', 'description', 'error']}
        label={
          <div className="flex items-center gap-x-1">
            <Input.Label
              required={allContractTypes?.find(it => it.value === contractType)?.greenhouseLinkCanBeEmpty !== true}
            >
              Greenhouse Link
            </Input.Label>
            <TooltipGreenhouseLink allContractTypes={allContractTypes} />
          </div>
        }
        placeholder="https://app.greenhouse.io/people/.."
      />

      <Controller
        control={control}
        name="hiringSource"
        render={({ field: { value } }) => (
          <div className="col-span-12 sm:col-span-6">
            <EmployeeHiringSourceSelectSingle
              error={errors.hiringSource?.message}
              label={
                <div className="flex items-center gap-x-1">
                  <Input.Label required={mode === FormAction.CREATING}>Hiring Source</Input.Label>
                  <TooltipHiringSource />
                </div>
              }
              value={value}
              onChange={newValue => setValue('hiringSource', newValue?.value ?? null, { shouldDirty: true })}
            />
          </div>
        )}
      />

      <div className="col-span-12 flex">
        <Checkbox
          checked={isNewGraduate}
          description="Applies to Employees in Tokyo Office Only"
          error={errors.isNewGraduate?.message}
          label="新卒採用"
          onChange={e => setValue('isNewGraduate', e.currentTarget.checked, { shouldDirty: true })}
        />
      </div>
    </fieldset>
  );
};

interface EmailCheckSectionProps {
  mode?: FormAction;
  emailCheckStatus?: EmailCheckStatus;
  clearingEmployeeIds?: string[];
  onCheckAvailability?: () => Promise<void>;
}

const EmailCheckSection = ({
  mode,
  emailCheckStatus,
  clearingEmployeeIds,
  onCheckAvailability,
}: EmailCheckSectionProps) => {
  const isIdle = useMemo(() => emailCheckStatus === 'idle', [emailCheckStatus]);
  const isLoading = useMemo(() => emailCheckStatus === 'loading', [emailCheckStatus]);
  const isGwWarning = useMemo(
    () => emailCheckStatus === 'in_use_google_workspace' && mode === FormAction.EDITING,
    [emailCheckStatus, mode],
  );
  const isError = useMemo(
    () =>
      emailCheckStatus !== undefined && !['idle', 'loading', 'available'].includes(emailCheckStatus) && !isGwWarning,
    [emailCheckStatus, isGwWarning],
  );
  const styles = useMemo(() => {
    if (emailCheckStatus === 'available') {
      return {
        className: 'text-success',
        icon: 'check_circle',
        message: EMAIL_CHECK_MESSAGES.available,
      };
    } else if (isGwWarning) {
      return {
        className: 'text-orange-9',
        icon: 'warning',
        message: GW_EDITING_MESSAGE,
      };
    } else if (emailCheckStatus && isError) {
      return {
        className: 'text-failure',
        icon: 'error',
        message: EMAIL_CHECK_MESSAGES[emailCheckStatus],
      };
    }
  }, [emailCheckStatus, isGwWarning, isError]);

  return (
    <div className="mt-2">
      {(isIdle || isLoading) && (
        <Button
          className={classNames('w-full transition-colors duration-300', {
            'bg-green-7 hover:bg-green-8': isIdle,
            'bg-gray-4 hover:bg-gray-4': !isIdle,
          })}
          disabled={!isIdle}
          loading={isLoading}
          onClick={onCheckAvailability}
        >
          Check availability
        </Button>
      )}
      {styles && (
        <span className={classNames('flex items-start gap-1 text-xs', styles.className)}>
          <MaterialIcons iconName={styles.icon} className="mt-[2px]" />
          {styles.message}
        </span>
      )}
      {clearingEmployeeIds && clearingEmployeeIds.length > 0 && (
        <span className="flex items-start gap-1 text-xs">
          <MaterialIcons iconName="info" className="mt-[2px]" />
          <span>
            The email of&nbsp;
            {clearingEmployeeIds.map((id, index) => (
              <Fragment key={id}>
                {index > 0 && `, `}
                <EmployeeLink employeeId={id} openInNew={true}>
                  {id}
                </EmployeeLink>
              </Fragment>
            ))}
            &nbsp;will be cleared.
          </span>
        </span>
      )}
    </div>
  );
};
